import { fromUnixTime } from 'date-fns';
import { format } from 'date-fns-tz';
import React from 'react';
import { type TooltipProps } from 'recharts';
import {
  type NameType,
  type ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { type SchemaElement } from '#/app/models/sync-line-chart/types.ts';
import { MarkLegend } from './line-chart-legend.tsx';

type Mark = SchemaElement['marks'][0];
export type SiblingChartTooltip = {
  schemaElement: SchemaElement;
  payload: TooltipProps<ValueType, NameType>['payload'];
};
export function LineChartTooltip(
  props: TooltipProps<ValueType, NameType> & {
    schemaElement: SchemaElement;
    siblingChart?: SiblingChartTooltip;
    parentDimensions: DOMRect;
  },
) {
  const {
    active,
    payload,
    schemaElement,
    label,
    siblingChart,
    coordinate,
    parentDimensions,
  } = props;
  if (!active || payload == null || label == null) {
    return null;
  }

  const markByDataKey = schemaElement.marks.reduce(
    (acc, mark) => {
      acc[mark.dataKey] = mark;
      return acc;
    },
    {} as Record<string, Mark>,
  );

  const siblingMarkByDataKey = siblingChart?.schemaElement.marks.reduce(
    (acc, mark) => {
      if (mark.dashStyle === 'pattern') {
        return acc;
      }
      acc[mark.dataKey] = mark;
      return acc;
    },
    {} as Record<string, Mark>,
  );

  const alerts = payload.filter(
    (d) => d.dataKey === 'alert' && d.payload.alertId != null,
  );
  const alertsLabel = alerts.length > 1 ? 'Incidencias' : 'Incidencia';
  const image = payload.find((d) => Boolean(d.payload.foto))?.payload.foto;

  const mainPayload = payload.filter((d) => d.type !== 'none');
  const siblingPayload =
    siblingChart?.payload?.filter((d) => d.type !== 'none') ?? [];
  const hasSibling = siblingPayload.length > 0;

  const tooltipWidth = 424;
  let x = (coordinate?.x ?? 0) + 15;
  const y = (coordinate?.y ?? 0) - 200;

  if (x - 60 > parentDimensions.width / 2) {
    x = x - parentDimensions.width / 2 - tooltipWidth / 3 - 15;
  }

  const date = fromUnixTime(label);
  const time = format(date, 'HH:mm', { timeZone: 'Europe/Madrid' });
  const dayMonth = format(date, 'dd/MM', { timeZone: 'Europe/Madrid' });

  return (
    <div
      className="z-50 rounded-sm bg-accent px-3 py-6"
      style={{
        position: 'absolute',
        width: tooltipWidth,
        left: x,
        top: y,
      }}
    >
      {(hasSibling || mainPayload.length > 0) && (
        <div className="mb-6 flex gap-x-9">
          {hasSibling && (
            <div>
              <p className="mb-4 text-body text-primary">
                {siblingChart?.schemaElement.yAxis?.label}
              </p>
              <ul className="flex flex-col gap-y-4">
                {siblingPayload?.map((d: any) => {
                  const mark = (siblingMarkByDataKey as Record<string, Mark>)[
                    d.dataKey
                  ];

                  if (!mark || mark.dashStyle === 'pattern') {
                    return null;
                  }
                  return (
                    <li
                      key={d.dataKey}
                      className="flex items-end justify-start gap-x-2"
                    >
                      <MarkLegend mark={mark} />
                      <p className="text-primary">
                        {d.value} {mark.unit}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div>
            <p className="mb-4 text-body text-primary">
              {schemaElement.yAxis?.label}
            </p>
            <ul className="flex flex-col gap-y-4">
              {mainPayload.map((d: any) => {
                const mark = markByDataKey[d.dataKey];
                return (
                  <li
                    key={d.dataKey}
                    className="flex items-end justify-start gap-x-2"
                  >
                    <MarkLegend mark={mark} />
                    <p className="text-primary">
                      {d.value} {mark.unit}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      {image && (
        <div className="mb-4 flex h-[224] w-[400] justify-center overflow-hidden rounded-sm">
          <img
            src={image}
            alt="Imagen"
            className="object-contain object-center"
          />
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex">
          {alerts.length > 0 && (
            <>
              <div className="relative">
                <MarkLegend mark={markByDataKey.alert} tooltip={true} />
              </div>
              <p className="flex flex-col">
                <span className="text-label text-primary">
                  {alerts.length} {alertsLabel}
                </span>
                <span className="text-label text-warning">
                  {alerts.map((a) => `# ${a.payload.alertId}`).join(', ')}
                </span>
              </p>
            </>
          )}
        </div>
        <p className="text-right text-lg text-primary">
          {time}h {dayMonth}
        </p>
      </div>
    </div>
  );
}
